/**
 * "Outgoing RFQ template (protected Excel)" (prototype `vSuppliers`, the `tmpl` block).
 *
 * What the supplier actually received: item code, name, qty — and a locked unit-price column
 * they fill in. The prototype shows one flat table; since a real enquiry produces one RFQ per
 * supplier with different lines in each, the supplier is selectable and the table shows that
 * RFQ's own template.
 *
 * The file itself is generated by the backend (infra C.3 queues it); this is a preview of its
 * contents, with `excel_url` linking to the real thing.
 */

import { fileHref } from '../../lib/api/fileUrl'
import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { FileLink } from '../../components/FileLink'
import { Card } from '../../components/ui'
import { formatAgo } from '../../lib/format'
import type { Rfq } from '../../lib/types'

export function RfqTemplateCard({ rfqs }: { rfqs: Rfq[] }) {
  const [selected, setSelected] = useState<string | null>(null)
  if (rfqs.length === 0) return null

  const active = rfqs.find((r) => r.id === selected) ?? rfqs[0]
  const items = active.items ?? []

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b>Outgoing RFQ template (protected Excel)</b>
        <select value={active.id} onChange={(e) => setSelected(e.target.value)} style={{ marginLeft: 'auto' }}>
          {rfqs.map((rfq) => (
            <option key={rfq.id} value={rfq.id}>
              {rfq.id} · {rfq.supplier_name ?? rfq.supplier}
            </option>
          ))}
        </select>
      </div>

      <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 6 }}>
        Sent to <b style={{ color: 'var(--ink)' }}>{active.email_to || active.supplier_name || active.supplier}</b>
        {active.sent_at ? ` · sent ${formatAgo(active.sent_at)}` : null} ·{' '}
        {active.excel_url ? (
          /*
            Through `fileHref`, never the raw `/files/…` path (2026-09-18): on the deployed app
            the browser talks to Frappe cross-origin, so a relative path lands on OUR origin and
            the SPA shell answers instead of the workbook. Same route mail attachments use.
          */
          <FileLink href={fileHref(active.excel_url) ?? active.excel_url} name={`${active.id}.xlsx`}>
            {active.id}.xlsx
          </FileLink>
        ) : (
          <span>file pending</span>
        )}
      </div>

      <table style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>Item Code</th>
            <th>Item Name</th>
            <th className="right">Qty</th>
            <th className="right">Unit Price (supplier fills)</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ color: 'var(--muted)', textAlign: 'center', padding: 22 }}>
                No lines are addressed to {active.supplier_name ?? active.supplier} — assign them in the RFQ triage.
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <tr key={item.code}>
                <td className="mono">{item.code}</td>
                <td>{item.name}</td>
                <td className="right mono">
                  {item.qty} {item.uom}
                </td>
                {/* The one unlocked cell in the protected sheet. */}
                <td className="right" style={{ color: 'var(--muted)' }}>
                  ▢ unlocked
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {items.length > 0 ? (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <Badge tone="muted">
            {items.length} line{items.length === 1 ? '' : 's'}
          </Badge>{' '}
          <span style={{ color: 'var(--muted)' }}>
            Every other cell is locked, so replies parse back reliably.
          </span>
        </div>
      ) : null}
    </Card>
  )
}
